import axios from 'axios'
import { ElMessage } from 'element-plus'
import API_CONFIG from '../config/api.js'

// 创建axios实例
const api = axios.create({
  baseURL: API_CONFIG.API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
api.interceptors.request.use(
  config => {
    // 添加认证token
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // 添加用户信息到请求头（用于权限验证）
    const user = localStorage.getItem('user')
    const family = localStorage.getItem('family')
    if (user && user !== 'null') {
      try {
        const userData = JSON.parse(user)
        if (userData && userData.id) {
          config.headers['X-User-Id'] = userData.id
          console.log('Request interceptor: Setting X-User-Id =', userData.id)
        } else {
          console.warn('Request interceptor: User data is invalid or missing id')
        }
      } catch (error) {
        console.error('Request interceptor: Failed to parse user data', error)
        localStorage.removeItem('user')
      }
    } else {
      console.warn('Request interceptor: No user found in localStorage')
    }
    if (family && family !== 'null') {
      try {
        const familyData = JSON.parse(family)
        if (familyData && familyData.id) {
          config.headers['X-Family-Id'] = familyData.id
          console.log('Request interceptor: Setting X-Family-Id =', familyData.id)
        } else {
          console.warn('Request interceptor: Family data is invalid or missing id')
        }
      } catch (error) {
        console.error('Request interceptor: Failed to parse family data', error)
        localStorage.removeItem('family')
      }
    } else {
      console.warn('Request interceptor: No family found in localStorage')
    }
    
    return config
  },
  error => {
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  response => {
    const { data } = response
    if (data.success === false) {
      ElMessage.error(data.message || '请求失败')
      return Promise.reject(new Error(data.message || '请求失败'))
    }
    return data
  },
  error => {
    console.error('Response error:', error)
    
    // 对于特定请求的错误，不显示错误提示（如轮询请求）
    if (error.config && error.config.url && error.config.url.includes('/pending-count')) {
      if (error.code === 'ECONNABORTED') {
        console.warn('轮询请求超时，忽略错误提示')
      } else if (error.message === 'canceled' || error.code === 'ERR_CANCELED') {
        console.log('轮询请求被取消，忽略错误提示')
      }
      return Promise.reject(error)
    }
    
    let message = '网络错误'
    
    if (error.response) {
      const { status, data } = error.response
      switch (status) {
        case 400:
          // 处理验证错误
          if (data.code && Array.isArray(data.code)) {
            message = data.code.join(', ')
          } else if (data.message) {
            message = data.message
          } else {
            message = '请求参数错误'
          }
          break
        case 401:
          message = '未授权，请重新登录'
          localStorage.removeItem('token')
          // 可以跳转到登录页面
          break
        case 403:
          message = '禁止访问'
          break
        case 404:
          message = '请求的资源不存在'
          break
        case 500:
          message = '服务器内部错误'
          break
        default:
          message = data.message || `错误 ${status}`
      }
    } else if (error.request) {
      if (error.code === 'ECONNABORTED') {
        message = '请求超时，请检查网络连接'
      } else {
        message = '网络连接失败'
      }
    }
    
    ElMessage.error(message)
    return Promise.reject(error)
  }
)

// 产品相关API
export const productApi = {
  // 获取产品列表
  getProducts(params = {}) {
    return api.get('/products', { params })
  },
  
  // 获取产品详情
  getProduct(id) {
    return api.get(`/products/${id}`)
  },
  
  // 创建产品
  createProduct(data) {
    return api.post('/products', data)
  },
  
  // 更新产品
  updateProduct(id, data) {
    return api.put(`/products/${id}`, data)
  },
  
  // 删除产品
  deleteProduct(id) {
    return api.delete(`/products/${id}`)
  },
  
  // 更新产品状态
  updateProductStatus(id, status) {
    return api.put(`/products/${id}/status`, { status })
  },
  
  // 更新产品脚本
  updateProductScript(id, script) {
    return api.put(`/products/${id}/script`, { script })
  },
  
  // 复制产品
  duplicateProduct(id) {
    return api.post(`/products/${id}/duplicate`)
  },
  
  // 获取产品分类
  getCategories() {
    return api.get('/products/categories')
  },
  
  // 获取设备类型
  getDeviceTypes() {
    return api.get('/products/device-types')
  },
  
  // 获取制造商
  getManufacturers() {
    return api.get('/products/manufacturers')
  },
  
  // 获取产品统计
  getStatistics() {
    return api.get('/products/statistics')
  }
}

// 设备类型相关API
export const deviceTypeApi = {
  // 获取设备类型列表
  getDeviceTypes(params = {}) {
    return api.get('/device-types', { params })
  },
  
  // 获取设备类型详情
  getDeviceType(id) {
    return api.get(`/device-types/${id}`)
  },
  
  // 创建设备类型
  createDeviceType(data) {
    return api.post('/device-types', data)
  },
  
  // 更新设备类型
  updateDeviceType(id, data) {
    return api.put(`/device-types/${id}`, data)
  },
  
  // 删除设备类型
  deleteDeviceType(id) {
    return api.delete(`/device-types/${id}`)
  },
  
  // 复制设备类型
  duplicateDeviceType(id) {
    return api.post(`/device-types/${id}/duplicate`)
  },
  
  // 获取设备类型统计
  getStatistics() {
    return api.get('/device-types/statistics')
  }
}

// 家庭场所相关API
export const familyLocationApi = {
  // 获取家庭场所列表
  getFamilyLocations(familyId) {
    return api.get(`/families/${familyId}/locations`)
  },
  
  // 获取场所详情
  getLocationById(familyId, locationId) {
    return api.get(`/families/${familyId}/locations/${locationId}`)
  },
  
  // 创建场所
  createLocation(familyId, data) {
    return api.post(`/families/${familyId}/locations`, data)
  },
  
  // 更新场所
  updateLocation(familyId, locationId, data) {
    return api.put(`/families/${familyId}/locations/${locationId}`, data)
  },
  
  // 删除场所
  deleteLocation(familyId, locationId) {
    return api.delete(`/families/${familyId}/locations/${locationId}`)
  },
  
  // 检查场所名称是否可用
  checkLocationName(familyId, name, excludeId = null) {
    const params = { name }
    if (excludeId) {
      params.excludeId = excludeId
    }
    return api.get(`/families/${familyId}/locations/check-name`, { params })
  },
  
  // 获取场所统计
  getLocationStatistics(familyId) {
    return api.get(`/families/${familyId}/locations/statistics`)
  },
  
  // 刷新设备数量统计
  refreshDeviceCounts(familyId) {
    return api.post(`/families/${familyId}/locations/refresh-device-counts`)
  }
}

// 设备相关API
export const deviceApi = {
  // 获取设备列表
  getDevices(params = {}) {
    return api.get('/devices/all', { params })
  },
  
  // 获取家庭设备
  getFamilyDevices(familyId, params = {}) {
    return api.get(`/devices/family/${familyId}`, { params })
  },
  
  // 获取家庭关联的设备
  getDevicesByFamily(familyId) {
    return api.get(`/devices/family/${familyId}`)
  },
  
  // 通过设备ID获取设备详情
  getDeviceByDeviceId(deviceId) {
    return api.get(`/devices/${deviceId}`)
  },
  
  // 获取设备详情
  getDevice(deviceId) {
    return api.get(`/devices/${deviceId}`)
  },
  
  // 创建设备
  createDevice(data) {
    return api.post('/devices', data)
  },
  
  // 更新设备
  updateDevice(id, data) {
    return api.put(`/devices/${id}`, data)
  },
  
  // 删除设备
  deleteDevice(id) {
    return api.delete(`/devices/${id}`)
  },
  
  // 批量创建设备
  createDeviceBatch(devices) {
    return api.post('/devices/batch', devices)
  },
  
  // 批量删除设备
  deleteDeviceBatch(deviceIds) {
    return api.delete('/devices/batch', { data: deviceIds })
  },
  
  // 复制设备
  duplicateDevice(id) {
    return api.post(`/devices/${id}/duplicate`)
  },
  
  // 添加设备到家庭
  addDeviceToFamily(familyId, data) {
    return api.post(`/devices/family/${familyId}`, data)
  },
  
  // 绑定设备到家庭 (旧接口，保持兼容)
  bindDeviceToFamilyOld(deviceId, familyId, data) {
    return api.post(`/devices/${deviceId}/bind-family/${familyId}`, data)
  },
  
  // 更新设备信息
  updateDeviceInfo(deviceId, data) {
    return api.put(`/devices/${deviceId}/info`, data)
  },
  
  // 更新设备通知策略
  updateDeviceNotificationPolicy(deviceId, notificationPolicyId) {
    return api.put(`/devices/${deviceId}/notification-policy`, { notificationPolicyId })
  },
  
  // 获取设备类型列表
  getDeviceTypes() {
    return api.get('/devices/types')
  },
  
  // 设备布防/撤防
  toggleDeviceArm(deviceId, armStatus) {
    return api.post(`/devices/${deviceId}/arm`, { armStatus })
  },
  
  // 全部布防
  armAllDevices(familyId) {
    return api.post(`/devices/family/${familyId}/arm-all`)
  },
  
  // 全部撤防
  disarmAllDevices(familyId) {
    return api.post(`/devices/family/${familyId}/disarm-all`)
  },
  
  // 获取设备类型
  getDeviceTypes() {
    return api.get('/devices/types')
  },
  
  // 获取设备统计
  getStatistics(familyId) {
    return api.get(`/devices/family/${familyId}/statistics`)
  },
  
  // 搜索设备用于绑定
  searchDeviceForBinding(deviceCode) {
    return api.get(`/devices/search/${deviceCode}`)
  },
  
  // 绑定设备到家庭
  bindDeviceToFamily(deviceId, data) {
    return api.post(`/devices/${deviceId}/bind`, data)
  },
  
  // 解绑设备
  unbindDevice(deviceId) {
    return api.delete(`/devices/${deviceId}/unbind`)
  }
}

// 家庭相关API
export const familyApi = {
  // 获取家庭列表
  getFamilies() {
    return api.get('/families')
  },
  
  // 获取用户家庭
  getUserFamilies(userId) {
    return api.get(`/families/user/${userId}`)
  },
  
  // 获取家庭详情
  getFamily(id) {
    return api.get(`/families/${id}`)
  },
  
  // 创建家庭
  createFamily(data) {
    return api.post('/families', data)
  },
  
  // 更新家庭
  updateFamily(id, data) {
    return api.put(`/families/${id}`, data)
  },
  
  // 删除家庭
  deleteFamily(id) {
    return api.delete(`/families/${id}`)
  },
  
  // 获取家庭成员
  getFamilyMembers(familyId) {
    return api.get(`/families/${familyId}/members`)
  },
  
  // 添加家庭成员
  addFamilyMember(familyId, data) {
    return api.post(`/families/${familyId}/members`, data)
  },
  
  // 更新家庭成员
  updateFamilyMember(familyId, userId, data) {
    return api.put(`/families/${familyId}/members/${userId}`, data)
  },
  
  // 移除家庭成员
  removeFamilyMember(familyId, userId, operatorId) {
    return api.delete(`/families/${familyId}/members/${userId}`, {
      params: { operatorId }
    })
  },
  
  // 获取紧急联系人列表
  getEmergencyContacts(familyId) {
    return api.get(`/families/${familyId}/emergency-contacts`)
  },
  
  // 创建紧急联系人
  createEmergencyContact(familyId, data) {
    return api.post(`/families/${familyId}/emergency-contacts`, data)
  },
  
  // 更新紧急联系人
  updateEmergencyContact(familyId, contactId, data) {
    return api.put(`/families/${familyId}/emergency-contacts/${contactId}`, data)
  },
  
  // 删除紧急联系人
  deleteEmergencyContact(familyId, contactId) {
    return api.delete(`/families/${familyId}/emergency-contacts/${contactId}`)
  },
  
  // 获取高优先级紧急联系人
  getHighPriorityContacts(familyId) {
    return api.get(`/families/${familyId}/emergency-contacts/high-priority`)
  },
  
  // 手动创建家庭所有者成员关系（用于修复缺失的成员关系）
  createOwnerMember(familyId) {
    return api.post(`/families/${familyId}/create-owner-member`)
  },
  
  // 更新家庭全局通知策略
  updateGlobalNotificationPolicy(familyId, policyId) {
    return api.put(`/families/${familyId}/global-notification-policy`, { policyId })
  }
}

// 通知策略API
export const notificationPolicyApi = {
  // 根据家庭ID获取通知策略列表
  getNotificationPoliciesByFamilyId(familyId) {
    return api.get(`/notification-policies/family/${familyId}`)
  },

  // 根据家庭ID获取已启用的通知策略列表
  getEnabledNotificationPolicies(familyId) {
    return api.get(`/notification-policies/family/${familyId}/enabled`)
  },

  // 根据ID获取通知策略详情
  getNotificationPolicyById(id) {
    return api.get(`/notification-policies/${id}`)
  },

  // 创建通知策略
  createNotificationPolicy(policyData) {
    return api.post('/notification-policies', policyData)
  },

  // 更新通知策略
  updateNotificationPolicy(id, policyData) {
    return api.put(`/notification-policies/${id}`, policyData)
  },

  // 删除通知策略
  deleteNotificationPolicy(id) {
    return api.delete(`/notification-policies/${id}`)
  },

  // 切换策略启用状态
  togglePolicyEnabled(id) {
    return api.patch(`/notification-policies/${id}/toggle`)
  }
}

// 设备事件通知策略API
export const deviceEventPolicyApi = {
  // 获取设备所有事件策略配置
  getDeviceEventPolicies(deviceId) {
    return api.get(`/device-event-policies/device/${deviceId}`)
  },

  // 批量保存设备事件策略配置
  batchSaveDeviceEventPolicies(deviceId, policies) {
    return api.post(`/device-event-policies/device/${deviceId}/batch`, policies)
  },

  // 删除单个事件策略配置
  deleteDeviceEventPolicy(deviceId, eventType) {
    return api.delete(`/device-event-policies/device/${deviceId}/event/${eventType}`)
  },

  // 根据产品ID获取事件类型列表
  getEventTypesByProductId(productId) {
    return api.get('/device-event-policies/event-types', { params: { productId } })
  }
}

// 产品事件管理API（管理端用）
export const productEventApi = {
  // 根据产品ID获取事件列表
  getEventsByProductId(productId) {
    return api.get(`/product-events/product/${productId}`)
  },

  // 创建事件
  createEvent(event) {
    return api.post('/product-events', event)
  },

  // 更新事件
  updateEvent(id, event) {
    return api.put(`/product-events/${id}`, event)
  },

  // 删除事件
  deleteEvent(id) {
    return api.delete(`/product-events/${id}`)
  },

  // 初始化产品默认事件（根据产品名称匹配预定义模板）
  initDefaultEvents(productId) {
    return api.post(`/product-events/init-defaults/${productId}`)
  }
}

// 设备设置API（跌倒雷达等设备参数持久化）
export const deviceSettingApi = {
  getSettings(deviceId) {
    return api.get(`/device-settings/${deviceId}`)
  },
  saveSetting(deviceId, key, value) {
    return api.put(`/device-settings/${deviceId}`, { key, value: String(value) })
  }
}

// 控制台相关API
export const dashboardApi = {
  // 获取控制台统计数据
  getStatistics() {
    return api.get('/dashboard/statistics')
  },
  
  // 获取设备状态图表数据
  getDeviceStatusChart() {
    return api.get('/dashboard/device-status-chart')
  },
  
  // 获取告警趋势图表数据
  getAlarmTrendChart(days = 7) {
    return api.get('/dashboard/alarm-trend-chart', { params: { days } })
  },
  
  // 获取最近告警
  getRecentAlarms(limit = 10) {
    return api.get('/dashboard/recent-alarms', { params: { limit } })
  }
}

export default api